import React from "react";
import { FileSpreadsheetIcon, UploadIcon } from "lucide-react";
import { SettingsBudgetTable } from "./SettingsBudgetTable";
import { useGetBudget } from "@/hooks/useGetBudget";
import * as Sentry from "@sentry/react";
import { BudgetHeader } from "./BudgetHeader";
import { BudgetDialog } from "./BudgetDialog";
import { BudgetLoading } from "./BudgetLoading";
import { BudgetProvider } from "./BudgetProvider";
import { useBudgetContext } from "./useBudgetContext";
import { useAuth } from "@/Auth";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { useT } from "@/i18n";

export function SettingsBudget() {
  return (
    <BudgetProvider>
      <SettingsBudgetContent />
    </BudgetProvider>
  );
}

function SettingsBudgetContent() {
  const isDemo = useAuth().user?.is_demo ?? false;
  const { selectedYear, selectedResolution, budgetData, setBudgetData, selectedBusinessId } =
    useBudgetContext();
  const [showUploadDialog, setShowUploadDialog] = React.useState(false);
  const { t } = useT();

  const budget = useGetBudget(selectedBusinessId || undefined, selectedResolution, selectedYear);

  React.useEffect(() => {
    if (budget.data) {
      setBudgetData(budget.data);
    } else {
      setBudgetData(null);
    }
  }, [budget.data, setBudgetData]);

  let content;

  if (budget.isLoading) {
    content = <BudgetLoading />;
  } else if (budget.isError) {
    Sentry.captureException(budget.error, {
      extra: {
        message: "budget.isSuccess is false",
        extra: { error: budget.error },
      },
    });
    content = null;
  } else if (!budgetData || budgetData.venue_budgets.length === 0) {
    content = (
      <div className="flex min-h-80 flex-1 items-center justify-center p-4">
        <Empty className="max-w-md border-0 p-0">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <FileSpreadsheetIcon />
            </EmptyMedia>
            <EmptyTitle>{t("budget.noBudget")}</EmptyTitle>
            <EmptyDescription>
              {isDemo ? t("budget.noBudgetDemo") : t("budget.provideXlsx")}
            </EmptyDescription>
          </EmptyHeader>
          {!isDemo && (
            <EmptyContent>
              <Button onClick={() => setShowUploadDialog(true)}>
                <UploadIcon data-icon="inline-start" />
                {t("budget.uploadXlsx")}
              </Button>
            </EmptyContent>
          )}
        </Empty>
        {!isDemo && (
          <BudgetDialog
            show={showUploadDialog}
            mode="upload"
            onClose={() => setShowUploadDialog(false)}
            businessId={selectedBusinessId!}
            year={selectedYear}
          />
        )}
      </div>
    );
  } else {
    content = (
      <SettingsBudgetTable
        businessId={selectedBusinessId!}
        year={selectedYear}
        resolution={selectedResolution}
      />
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <BudgetHeader />
      {content}
    </div>
  );
}
